import React, { ReactNode, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";

export type ModalProps = {
  children: ReactNode;
  isOpen: boolean;
  width?: string;
  padding?: string;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  preventCloseOnClickOutside?: boolean;
  /** Uses the full viewport on phones while preserving the desktop dialog. */
  mobileFullscreen?: boolean;
};

const Modal = ({
  children,
  isOpen,
  width,
  padding,
  setOpen,
  preventCloseOnClickOutside,
  mobileFullscreen = false,
}: ModalProps) => {
  const defaultWidth = width || "400px";
  const defaultPadding = padding || "20px";

  // 🧩 Responsive padding state
  const [responsivePadding, setResponsivePadding] = useState(defaultPadding);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        // mobile
        setResponsivePadding("16px");
        setIsMobile(true);
      } else {
        // desktop / tablet
        setResponsivePadding(defaultPadding);
        setIsMobile(false);
      }
    };

    handleResize(); // set initial
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [defaultPadding]);

  // 🛑 Stop background scroll
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={`fixed inset-0 flex z-[999] w-full bg-black/50 ${
            mobileFullscreen ? "items-stretch sm:items-center justify-center px-0 sm:px-6" : "items-center justify-center px-4 sm:px-6"
          }`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          {/* Backdrop click */}
          <div
            onClick={() => !preventCloseOnClickOutside && setOpen(false)}
            className="absolute inset-0 z-30"
          />

          {/* Modal content */}
          <motion.div
            style={{
              width: mobileFullscreen && isMobile ? "100%" : defaultWidth,
              padding: responsivePadding,
            }}
            className={`relative z-50 w-full overflow-y-auto bg-white ${
              mobileFullscreen
                ? "h-[100dvh] max-h-[100dvh] rounded-none shadow-none sm:h-fit sm:max-h-[90%] sm:rounded-[12px] sm:shadow-2xl"
                : "max-h-[90%] rounded-[12px] shadow-2xl"
            }`}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default Modal;
